import { Component, OnInit, OnDestroy } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Customer, MeteringPoint, MeteringPointGroup, UserSettings, UserInfo, DisruptionInfo, MeteringPointFilterSettings, MeteringPointSettings } from '@model';
import { Observable } from 'rxjs';
import { Store } from '@ngrx/store';
import { UserType, MeteringType, PermissionType } from '@enums';
import { SxPermissionService } from '@services';
import { ActivatedRoute, Router } from '@angular/router';

import * as combinedSelectors from '@appStore/metering-points/combined-selectors';
import * as combinedAccountSelectors from '@appStore/account/combined-selectors';
import * as fromStore from '@appStore/metering-points';
import * as fromCustomerStore from '@appStore/customers';
import * as fromAccountStore from '@appStore/account';
import * as sxUtilities from '@utils/sx-utilities';
import { filter, map } from 'rxjs/operators';
import { SubSink } from 'subsink';

@Component({
    selector: 'app-metering-points-display',
    templateUrl: './metering-points-display.component.html',
    standalone: false
})
export class MeteringPointsDisplayComponent implements OnInit, OnDestroy {
  customersWithMeteringPoints$: Observable<Customer[]>;
  customersWithDisruptions$: Observable<Customer[]>;
  meteringPoints$: Observable<MeteringPoint[]>;
  meteringPointGroups$: Observable<MeteringPointGroup[]>;
  meteringPointSettings$: Observable<MeteringPointSettings>;

  selectedHotMeteringPoints$: Observable<number[]>;
  selectedColdMeteringPoints$: Observable<number[]>;
  activatedHotVirtualGroup$: Observable<boolean>;
  activatedColdVirtualGroup$: Observable<boolean>;

  customersLoaded$: Observable<boolean>;
  meteringPointsLoaded$: Observable<boolean>;
  meteringPointGroupsLoaded$: Observable<boolean>;

  userSettings$: Observable<UserSettings>;
  selectTabIndex$: Observable<number>;

  showMeteringPointGroups: boolean;
  selectedTabIndex = 0;
  hotMeteringPointsCount: number;
  coldMeteringPointsCount: number;
  allMeteringPointsCount: number;
  countSet = false;

  plannedDisruptions = 0;
  unPlannedDisruptions = 0;
  numberOfMeteringTypes = 0;

  hasHotMeteringPoints: boolean;
  hasColdMeteringPoints: boolean;
  
  private subs = new SubSink();
  userInfo$: Observable<UserInfo>;
  isMobile$: Observable<boolean>;

  constructor(
    private store: Store<fromStore.State>,
    private customersStore: Store<fromCustomerStore.State>,
    private accountStore: Store<fromAccountStore.AccountState>,
    private sxPermissionService: SxPermissionService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private breakpointObserver: BreakpointObserver
  ) {}

  ngOnInit() {
    this.store.select(fromStore.getMeteringPointsWithDisruptions).subscribe(mps => {
      this.disruptionsLoaded(mps);
    });
    
    this.customersWithMeteringPoints$ = this.customersStore.select(combinedSelectors.getFilledCustomers);
    this.customersLoaded$ = this.customersStore.select(fromCustomerStore.getCustomersLoaded);
    this.customersWithDisruptions$ = this.customersStore.select(combinedSelectors.getFilledCustomersWithDisruptions);
    this.meteringPoints$ = this.store.select(fromStore.getMeteringPoints);
    this.meteringPointsLoaded$ = this.store.select(fromStore.getMeteringPointsLoaded);

    let virtualGroup = this.store.select(fromStore.getVirtualGroup);

    this.activatedHotVirtualGroup$ = virtualGroup.pipe(map(x => x.hotActivated));
    this.activatedColdVirtualGroup$ = virtualGroup.pipe(map(x => x.coldActivated));
    this.selectedHotMeteringPoints$ = virtualGroup.pipe(map(x => x.hot));
    this.selectedColdMeteringPoints$ = virtualGroup.pipe(map(x => x.cold));

    this.meteringPointGroups$ = this.store.select(combinedSelectors.getFilledMeteringPointGroups);
    this.meteringPointGroupsLoaded$ = this.store.select(fromStore.getMeteringPointGroupsLoaded);

    this.userSettings$ = this.accountStore.select(combinedAccountSelectors.getFilledSettings);
    this.userInfo$ = this.accountStore.select(fromAccountStore.getUserInfo);
    this.meteringPointSettings$ = this.store.select(fromStore.getMeteringPointSettings);

    this.selectTabIndex$ = this.store.select(fromStore.getSelectedMeteringPointsTabIndex);

    this.showMeteringPointGroups = this.sxPermissionService.securityTrimByUserType([
      UserType.Company,
      UserType.Brf,
      UserType.Manager
    ]);

    this.showMeteringPointGroups =
      this.showMeteringPointGroups &&
      this.sxPermissionService.checkPermissions([PermissionType.ManageMeteringPointGroup], null);

    this.subs.sink = this.meteringPoints$.pipe(filter(x => !!x)).subscribe(meteringPoints => {
      this.hasHotMeteringPoints = meteringPoints.find(m => m.meteringType === MeteringType.Hot) !== undefined;
      this.hotMeteringPointsCount = meteringPoints.filter(mp => mp.meteringType === MeteringType.Hot).length;
      this.hasColdMeteringPoints = meteringPoints.find(m => m.meteringType === MeteringType.Cold) !== undefined;
      this.coldMeteringPointsCount = meteringPoints.filter(mp => mp.meteringType === MeteringType.Cold).length;
      this.allMeteringPointsCount = this.hotMeteringPointsCount + this.coldMeteringPointsCount;
      this.countSet = true;
      this.numberOfMeteringTypes = 0;
      
      if (this.hasHotMeteringPoints) {
        this.numberOfMeteringTypes += 1;
      }
      if (this.hasColdMeteringPoints) {
        this.numberOfMeteringTypes += 1;
      }

      if (this.allMeteringPointsCount === 1) {
        this.router.navigate(['/secure/meteringpoints/' + meteringPoints[0].id], {relativeTo: this.activatedRoute, replaceUrl: true});
      }
    });
    this.isMobile$ = this.breakpointObserver.observe("(max-width: 576px)").pipe(map((result) => result.matches));
    
    this.setSelectedTab();
  }

  setSelectedTab = async () => {
    if (this.activatedRoute.snapshot.url.length === 0) {
      this.selectedTabIndex = 0;
      return;
    }

    switch (this.activatedRoute.snapshot.url[0].path) {
      case 'groups': {
        this.selectedTabIndex = 1;
        break;
      }
      case 'disruptions': {
        await this.delay(1); // Utan denna fuling ser tabben inte ut att vara aktiv  :-)
        this.selectedTabIndex = 2;
        break;
      }
      default: {
        this.selectedTabIndex = 0;
        break;
      }
    }
  };

  disruptionsLoaded = (disruptions: MeteringPoint[]) => {
    this.plannedDisruptions = 0;
    this.unPlannedDisruptions = 0;
    this.setSelectedTab();
    let disruptionsInfos: DisruptionInfo[] = new Array<DisruptionInfo>();

    disruptions.forEach(d => {
      if (d.disruptionInfos && d.disruptionInfos.length > 0){
        disruptionsInfos = [...disruptionsInfos, ...d.disruptionInfos];
      }
    });
    disruptionsInfos.forEach(d => {
      if (d.isPlanned) {
        this.plannedDisruptions++;
      } else {
        this.unPlannedDisruptions++;
      }
    })
  };

  private delay(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  fixTabs(event) {
    sxUtilities.fixInnerMatGroupTabs();
  }

  onTabIndexChange = (ix: number) => {
    this.store.dispatch(new fromStore.SelectTabIndexMeteringPoints(ix));
    this.store.dispatch(new fromStore.ClearScrollPosition());
  };

  onMeteringPointSelected = (selectedMp: MeteringPoint) => {
    this.store.dispatch(new fromStore.SelectMeteringPoint(selectedMp));

    this.router.navigate([`/secure/meteringpoints/${selectedMp.id}`]);
  };
}
